/**
 * Appeals (Console v2 §3.3, user-management §5.5): "Ask us to review this"
 * for a staff warning, feature limit or suspension, once per action, within
 * 180 days, up to 1000 characters. Someone other than the person who took
 * the action decides. Signed in it goes to `users/@me/appeals`; a
 * suspended account, signed out, appeals with its sign-in refusal's token
 * (`auth/appeal`). A decided appeal is shown once, with what staff wrote.
 */
import {
  APPEAL_STATEMENT_MAX,
  ApiErrorCode,
  Routes,
  type AppealViolationType,
  type AuthAppealRequest,
  type CreateAppealRequest,
  type UserAppeal,
} from '@neosocial/protocol';
import { useState } from 'react';
import { create } from 'zustand';
import { useT } from '../i18n';
import { api, isApiRequestError, type ApiRequestError } from '../lib/api';
import { errorText } from '../mod/actions';
import { Btn, Icon } from '../ui/kit';
import { Dialog } from '../ui/overlay';
import '../report/report.css';

export interface AppealTarget {
  type: AppealViolationType;
  /** The warning's id, or the action's; signed out, the suspension the token names. */
  violationId?: string;
  /** Signed out: the suspended sign-in's `nx_appeal_token`. */
  token?: string;
}

const useAppeal = create<{ target: AppealTarget | null }>(() => ({ target: null }));

export function openAppeal(target: AppealTarget): void {
  useAppeal.setState({ target });
}

/** Mounted once (shell/StaffNotices). */
export function AppealHost() {
  const target = useAppeal((s) => s.target);
  if (!target) return null;
  return <AppealDialog target={target} onClose={() => useAppeal.setState({ target: null })} />;
}

/** The suspended sign-in's token, when the refusal carries one. */
export function appealTokenOf(e: unknown): string | null {
  if (!isApiRequestError(e, ApiErrorCode.ACCOUNT_SUSPENDED)) return null;
  const tok = (e as ApiRequestError).body?.nx_appeal_token;
  return typeof tok === 'string' && tok ? tok : null;
}

export function AppealDialog({ target, onClose }: { target: AppealTarget; onClose(): void }) {
  const { t } = useT();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      if (target.token) {
        // `violation_id` empty: the suspension the token names
        const body: AuthAppealRequest = {
          appeal_token: target.token,
          statement: text.trim(),
          ...(target.violationId ? { violation_id: target.violationId } : {}),
        };
        await api.client.post<UserAppeal>(Routes.authAppeal(), body, { auth: false });
      } else {
        const body: CreateAppealRequest = {
          violation_id: target.violationId ?? '',
          statement: text.trim(),
        };
        await api.client.post<UserAppeal>(Routes.meAppeals(), body);
      }
      setDone(true);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={done ? t('ap.sentTitle') : t('ap.title')}
      icon={done ? 'check-circle' : 'shield'}
      size="md"
      cls="ns-ap-dlg"
      onClose={onClose}
      actions={
        done ? (
          <Btn kind="primary" size="sm" onClick={onClose}>
            {t('c.done')}
          </Btn>
        ) : (
          <>
            <Btn kind="ghost" size="sm" onClick={onClose}>
              {t('c.cancel')}
            </Btn>
            <Btn
              kind="primary"
              size="sm"
              busy={busy}
              disabled={!text.trim()}
              onClick={() => void send()}
            >
              {t('ap.send')}
            </Btn>
          </>
        )
      }
    >
      <div className="ns-ap">
        {done ? (
          <p>{t('ap.sent')}</p>
        ) : (
          <>
            <p>{t(`ap.intro.${target.type}`)}</p>
            <label className="ns-field">
              <span className="ns-label">{t('ap.statement')}</span>
              <textarea
                className="nx-textarea"
                dir="auto"
                rows={5}
                maxLength={APPEAL_STATEMENT_MAX}
                value={text}
                data-autofocus
                onChange={(e) => setText(e.target.value)}
              />
              <span className="ns-help">
                {t('rp.noteCount', { n: text.length, max: APPEAL_STATEMENT_MAX })}
              </span>
            </label>
            <p className="ns-help">{t('ap.who')}</p>
            {err ? (
              <p className="ns-err" role="alert">
                {err}
              </p>
            ) : null}
          </>
        )}
      </div>
    </Dialog>
  );
}

/** The sign-in screen of a suspended account: ask for a review without signing in. */
export function SuspendedAppeal({ token }: { token: string }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Btn kind="ghost" size="sm" icon="shield" className="ns-wide" onClick={() => setOpen(true)}>
        {t('ap.askReview')}
      </Btn>
      {open ? (
        <AppealDialog target={{ type: 'suspension', token }} onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
}

/* ---- outcomes ------------------------------------------------------------ */

const SEEN_KEY = 'nx.appeals.seen';

function seen(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

const useOutcomes = create<{ queue: UserAppeal[] }>(() => ({ queue: [] }));

/** At READY: decided appeals not shown on this device yet. */
export async function loadAppealOutcomes(): Promise<void> {
  try {
    const list = await api.client.get<UserAppeal[]>(Routes.meAppeals());
    const have = seen();
    useOutcomes.setState({ queue: list.filter((a) => a.state !== 'open' && !have.has(a.id)) });
  } catch {
    /* shown at the next start instead */
  }
}

export function AppealOutcome() {
  const { t } = useT();
  const a = useOutcomes((s) => s.queue[0]);
  if (!a) return null;
  const ok = () => {
    try {
      const have = seen();
      have.add(a.id);
      localStorage.setItem(SEEN_KEY, JSON.stringify([...have].slice(-200)));
    } catch {
      /* private mode: shown again next time */
    }
    useOutcomes.setState((s) => ({ queue: s.queue.filter((x) => x.id !== a.id) }));
  };
  return (
    <Dialog
      title={t('ap.outcomeTitle')}
      icon={a.state === 'reversed' ? 'check-circle' : 'shield'}
      size="sm"
      onClose={ok}
      actions={
        <Btn kind="primary" size="sm" onClick={ok}>
          {t('cfg.warn.ok')}
        </Btn>
      }
    >
      <div className="ns-ap">
        <p className="ns-ap-status">
          <Icon name={a.state === 'reversed' ? 'check-circle' : 'info'} />
          <span>{t(`ap.outcome.${a.state}`)}</span>
        </p>
        {a.note ? (
          <blockquote className="ns-warn-msg" dir="auto">
            {a.note}
          </blockquote>
        ) : null}
      </div>
    </Dialog>
  );
}
